/**
 * Reports storefront cart/checkout failures to PostHog and the Whisky Library backend
 * (POST /api/ops/cart-failure → Slack). Safe to call before PostHog SDK is ready.
 */
(function (window) {
  'use strict';

  var REPORTED = new WeakSet();
  var recentFingerprints = {};

  function markRecentlyReported(fp) {
    if (!fp) return;
    recentFingerprints[fp] = Date.now();
  }

  function recentlyReported(fp) {
    if (!fp) return false;
    var t = recentFingerprints[fp];
    if (!t) return false;
    if (Date.now() - t > 10000) {
      delete recentFingerprints[fp];
      return false;
    }
    return true;
  }

  function inferStage(errorMessage) {
    var msg = String(errorMessage || '').toLowerCase();
    if (/tier api|missing items|tier payload|no cart items/.test(msg)) return 'tier_fetch';
    if (/cart\/clear|clear\.js/.test(msg)) return 'cart_clear';
    if (/cart\/add|add\.js|cart add failed/.test(msg)) return 'cart_add';
    if (/empty|missing bundle|verify/.test(msg)) return 'cart_verify';
    return 'unknown';
  }

  function inferSource() {
    var path = window.location.pathname || '';
    if (document.querySelector('[data-mb-explorer-bundle-cta]')) return 'explorer_box_offer';
    if (document.querySelector('[data-mb-connoisseur-bundle-cta]')) return 'mystery_box_offer';
    if (document.getElementById('bourbon-subscription-form')) return 'replo_bourbon_subscription';
    if (path.indexOf('replo') !== -1 || document.querySelector('.alchemy__element')) return 'replo_landing';
    if (path.indexOf('taste-profile') !== -1) return 'taste_profile';
    if (path.indexOf('dragon-slayer') !== -1) return 'dragon_slayer';
    if (path.indexOf('father-day') !== -1) return 'father_day';
    if (path.indexOf('july-4') !== -1) return 'july_4_offer';
    if (path.indexOf('connoisseur-rebuttal') !== -1) return 'connoisseur_rebuttal';
    return 'storefront';
  }

  function getApiBase() {
    var base = typeof window.__QUIZ_API_BASE__ === 'string' ? window.__QUIZ_API_BASE__ : '';
    return String(base || '').replace(/\/$/, '');
  }

  function getOpsSecret() {
    return typeof window.__OPS_WEBHOOK_SECRET__ === 'string'
      ? window.__OPS_WEBHOOK_SECRET__.trim()
      : '';
  }

  function getQuizNavSource() {
    try {
      return sessionStorage.getItem('quiz_nav_source') || '';
    } catch (e) {
      return '';
    }
  }

  function getShopifyTemplate() {
    if (!window._template) return '';
    return [window._template.directory, window._template.name, window._template.suffix]
      .filter(Boolean)
      .join('.');
  }

  function generateCorrelationId() {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
    } catch (e) {}
    return 'cf_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
  }

  function getQuizSessionId() {
    try {
      return (window.localStorage.getItem('quiz_sessionId') || '').trim();
    } catch (e) {
      return '';
    }
  }

  function getTasteProfileResult() {
    var root = document.querySelector('.taste-profile-page');
    if (root) {
      var fromAttr = (root.getAttribute('data-taste-profile') || '').trim();
      if (fromAttr) return fromAttr;
    }
    var v3 = window.__TASTE_PROFILE_V3__;
    return (v3 && v3.tasteProfile ? String(v3.tasteProfile) : '').trim();
  }

  function getThemeRole() {
    try {
      return (
        (window.Shopify &&
          window.Shopify.theme &&
          window.Shopify.theme.role &&
          String(window.Shopify.theme.role)) ||
        ''
      );
    } catch (e) {
      return '';
    }
  }

  function getPosthogDistinctId() {
    try {
      if (window.posthog && typeof window.posthog.get_distinct_id === 'function') {
        return String(window.posthog.get_distinct_id() || '');
      }
    } catch (e) {}
    return '';
  }

  function getPosthogSessionId() {
    try {
      if (window.posthog && typeof window.posthog.get_session_id === 'function') {
        return String(window.posthog.get_session_id() || '');
      }
    } catch (e) {}
    return '';
  }

  function getDeviceContext() {
    var ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    var mobile = /iPhone|iPad|iPod|Android|Mobile/i.test(ua);
    var os = 'unknown';
    if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
    else if (/Android/.test(ua)) os = 'Android';
    else if (/Mac OS X/.test(ua)) os = 'macOS';
    else if (/Windows/.test(ua)) os = 'Windows';

    var browser = 'unknown';
    if (/Edg\//.test(ua)) browser = 'Edge';
    else if (/CriOS|Chrome\//.test(ua)) browser = mobile ? 'Mobile Chrome' : 'Chrome';
    else if (/FxiOS|Firefox\//.test(ua)) browser = 'Firefox';
    else if (/Safari\//.test(ua)) browser = mobile ? 'Mobile Safari' : 'Safari';

    return {
      browser: browser,
      os: os,
      device_type: mobile ? 'Mobile' : 'Desktop',
    };
  }

  function inferErrorKind(errorMessage, httpStatus, stage) {
    if (httpStatus != null && Number.isFinite(Number(httpStatus))) return 'http';
    var msg = String(errorMessage || '').toLowerCase();
    if (/load failed|failed to fetch|networkerror|network error|aborted|the operation was aborted/.test(msg)) {
      return 'network';
    }
    if (/\.js failed: \d+|\/cart\/[\w.]+\.js \d{3}/.test(msg)) return 'http';
    if (stage === 'tier_fetch') return 'application';
    return 'application';
  }

  function inferCartEndpoint(errorMessage, stage, explicitEndpoint) {
    if (explicitEndpoint) return String(explicitEndpoint);
    var msg = String(errorMessage || '');
    var match = msg.match(/\/cart\/[\w.]+\.js/);
    if (match) return match[0];
    if (stage === 'cart_clear') return '/cart/clear.js';
    if (stage === 'cart_add') return '/cart/add.js';
    if (stage === 'cart_verify') return '/cart.js';
    return '';
  }

  function funnelContext(extra) {
    var w = window.whiskyPosthog;
    if (w && typeof w.funnelContext === 'function') {
      return w.funnelContext(extra || {});
    }
    var ctx = {
      quiz_session_id: getQuizSessionId(),
      taste_profile_result: getTasteProfileResult(),
      shopify_template: window._template
        ? [window._template.directory, window._template.name, window._template.suffix]
            .filter(Boolean)
            .join('.')
        : undefined,
      page_path: window.location.pathname,
      page_url: window.location.href,
    };
    if (extra && typeof extra === 'object') {
      Object.keys(extra).forEach(function (key) {
        ctx[key] = extra[key];
      });
    }
    return ctx;
  }

  function normalizeError(err) {
    if (err instanceof Error) return err.message || String(err);
    if (typeof err === 'string') return err;
    try {
      return JSON.stringify(err);
    } catch (e) {
      return String(err);
    }
  }

  function fingerprint(ctx) {
    // The fingerprint identifies a failure; properties identify the segment.
    // tier_key used to be hashed in here, which split the same /cart/clear.js
    // 429 into one fingerprint per tier (fp_g63f9a, fp_12xg2c, fp_l8g488, ...)
    // and made one recurring issue look like six unrelated ones. tier_key
    // stays on the event as its own property.
    var parts = [
      ctx.source || '',
      ctx.stage || '',
      ctx.http_status != null ? String(ctx.http_status) : '',
      normalizeError(ctx.error_message || '')
        .toLowerCase()
        .replace(/\d{4,}/g, 'N'),
    ];
    var raw = parts.join('|');
    var hash = 0;
    for (var i = 0; i < raw.length; i++) {
      hash = (hash << 5) - hash + raw.charCodeAt(i);
      hash |= 0;
    }
    return 'fp_' + Math.abs(hash).toString(36);
  }

  // Shopify's own cart validation responses (e.g. a per-line "maximum
  // purchase quantity" rule) come back as a non-2xx /cart/add.js response,
  // but the message is already shown to the shopper (see PdpBuyBoxDefault's
  // inline status text) and there is no theme defect to act on. Without this
  // check every such expected response was reported identically to a real
  // failure (5xx, malformed request, etc.), paging #dev-alerts for normal
  // shopper behavior. Also matches wl-apps' own purchase-limit-validation
  // Function (cart_validations_generate_run.ts's two messages, "limit N per
  // customer — you can add N more." and "already reached the limit of N per
  // customer.") — activated 2026-09-14, so a shopper correctly hitting their
  // custom.max_qty_per_customer cap is exactly this same kind of expected,
  // by-design rejection, not a theme defect either. Shopify's "The product
  // 'X' is already sold out." is the same kind (2026-10-01): during the Silk
  // Velvet drop the bottle sold out while shoppers had its page open, and each
  // late tap paged #dev-alerts.
  var BENIGN_CART_VALIDATION_PATTERN =
    /maximum quantity of this item is already in your cart|limit \d+ per customer|already reached the limit of \d+ per customer|one of each bottle in .+ per customer|already bought this bottle|is already sold out/i;

  function isBenignCartValidationMessage(message) {
    return BENIGN_CART_VALIDATION_PATTERN.test(String(message || ''));
  }

  // Shopify's bot check answers cart calls with a 429 and a full HTML page
  // ("Verifying your connection...", 2026-09-02, a PDP, iOS Chrome). The first
  // 400 characters of that page are doctype, meta tags and inline CSS, which is
  // unreadable in Slack and a poor fingerprint input. A page body is reduced to
  // its <title>; a JSON body stays as it was. The check is labelled
  // error_kind 'bot_challenge' and still pages: a real shopper was blocked, and
  // a fetch cannot pass the check (a page load can, hence the refresh advice
  // in cart-add-merge.js).
  var CONNECTION_CHECK_PATTERN = /verifying your connection/i;

  function isConnectionCheck(bodyText) {
    return CONNECTION_CHECK_PATTERN.test(String(bodyText || ''));
  }

  function summarizeResponseBody(bodyText) {
    var text = String(bodyText || '');
    if (!/^\s*</.test(text)) return text.slice(0, 400);
    var title = text.match(/<title[^>]*>([^<]*)<\/title>/i);
    var label = title && title[1].trim() ? title[1].trim() : 'no title';
    return 'HTML page: ' + label;
  }

  function capturePosthog(ctx) {
    var props = funnelContext({
      source: ctx.source,
      stage: ctx.stage,
      error_message: normalizeError(ctx.error_message),
      http_status: ctx.http_status,
      shopify_response: ctx.shopify_response,
      variant_ids: ctx.variant_ids,
      tier_key: ctx.tier_key,
      funnel_mode: ctx.funnel_mode,
      failure_fingerprint: ctx.failure_fingerprint,
      cart_item_count: ctx.cart_item_count,
      posthog_distinct_id: ctx.posthog_distinct_id,
      posthog_session_id: ctx.posthog_session_id,
      correlation_id: ctx.correlation_id,
      theme_role: ctx.theme_role,
      quiz_nav_source: ctx.quiz_nav_source,
      error_kind: ctx.error_kind,
      cart_endpoint: ctx.cart_endpoint,
      clear_attempts: ctx.clear_attempts,
      browser: ctx.browser,
      os: ctx.os,
      device_type: ctx.device_type,
      screen_viewport: ctx.screen_viewport,
    });
    if (window.whiskyPosthog && typeof window.whiskyPosthog.capture === 'function') {
      window.whiskyPosthog.capture('cart_checkout_failed', props);
      return;
    }
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture('cart_checkout_failed', props);
    }
  }

  function postToBackend(ctx) {
    var base = getApiBase();
    var secret = getOpsSecret();
    if (!base || !secret) return;

    var body = {
      source: ctx.source,
      stage: ctx.stage,
      error_message: normalizeError(ctx.error_message),
      http_status: ctx.http_status,
      shopify_response: ctx.shopify_response,
      variant_ids: ctx.variant_ids,
      tier_key: ctx.tier_key,
      funnel_mode: ctx.funnel_mode,
      page_url: ctx.page_url || window.location.href,
      page_path: ctx.page_path || window.location.pathname,
      page_referrer: ctx.page_referrer || (typeof document !== 'undefined' ? document.referrer : ''),
      shopify_template: ctx.shopify_template || getShopifyTemplate(),
      quiz_session_id: ctx.quiz_session_id || getQuizSessionId(),
      quiz_nav_source: ctx.quiz_nav_source || getQuizNavSource(),
      taste_profile_result: ctx.taste_profile_result || getTasteProfileResult(),
      cart_item_count: ctx.cart_item_count,
      posthog_distinct_id: ctx.posthog_distinct_id,
      posthog_session_id: ctx.posthog_session_id,
      correlation_id: ctx.correlation_id,
      failure_fingerprint: ctx.failure_fingerprint,
      reported_at: ctx.reported_at || new Date().toISOString(),
      user_agent: ctx.user_agent || navigator.userAgent,
      screen_viewport:
        ctx.screen_viewport ||
        (typeof window !== 'undefined'
          ? String(window.innerWidth || '') + 'x' + String(window.innerHeight || '')
          : ''),
      theme_role: ctx.theme_role || getThemeRole(),
      error_kind: ctx.error_kind,
      cart_endpoint: ctx.cart_endpoint,
      browser: ctx.browser,
      os: ctx.os,
      device_type: ctx.device_type,
    };

    var url = base + '/api/ops/cart-failure';
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-ops-secret': secret,
      },
      body: JSON.stringify(body),
      credentials: 'omit',
      keepalive: true,
    }).catch(function () {});
  }

  /**
   * @param {object} ctx
   * @param {string} ctx.source
   * @param {string} ctx.stage
   * @param {string|Error} ctx.error_message
   */
  function report(ctx) {
    if (!ctx || !ctx.source || !ctx.stage) return;
    if (ctx.error && !ctx.error_message) {
      ctx.error_message = ctx.error;
    }

    var correlationId = ctx.correlation_id || generateCorrelationId();
    var stage = ctx.stage || inferStage(ctx.error_message);
    var errorMessage = normalizeError(ctx.error_message || 'Unknown cart failure');
    var device = getDeviceContext();
    var screenViewport =
      ctx.screen_viewport ||
      (typeof window !== 'undefined'
        ? String(window.innerWidth || '') + 'x' + String(window.innerHeight || '')
        : '');

    var payload = Object.assign({}, ctx, {
      error_message: errorMessage,
      correlation_id: correlationId,
      posthog_distinct_id: ctx.posthog_distinct_id || getPosthogDistinctId(),
      posthog_session_id: ctx.posthog_session_id || getPosthogSessionId(),
      failure_fingerprint: ctx.failure_fingerprint || fingerprint(ctx),
      theme_role: ctx.theme_role || getThemeRole(),
      stage: stage,
      shopify_template: ctx.shopify_template || getShopifyTemplate(),
      quiz_nav_source: ctx.quiz_nav_source || getQuizNavSource(),
      reported_at: ctx.reported_at || new Date().toISOString(),
      browser: ctx.browser || device.browser,
      os: ctx.os || device.os,
      device_type: ctx.device_type || device.device_type,
      screen_viewport: screenViewport,
      error_kind: ctx.error_kind || inferErrorKind(errorMessage, ctx.http_status, stage),
      cart_endpoint: inferCartEndpoint(errorMessage, stage, ctx.cart_endpoint),
    });

    var isBenignValidation = isBenignCartValidationMessage(errorMessage);
    if (isBenignValidation) {
      payload.error_kind = 'validation';
    }

    // Claim the token before the fingerprint check: a caller's report that the
    // fingerprint window then drops (a second tap within 10s) must still stop the
    // interceptor's deferred, generic report for the same attempt.
    if (ctx._reportToken && REPORTED.has(ctx._reportToken)) return;
    if (ctx._reportToken) REPORTED.add(ctx._reportToken);
    // The token is local bookkeeping, never part of what is sent.
    delete payload._reportToken;

    if (recentlyReported(payload.failure_fingerprint)) return;

    // Still captured to PostHog for analytics visibility, but a known-benign
    // Shopify cart-validation response never pages #dev-alerts. suppress_alert
    // works the same way: a caller that recovered (e.g. clear failed on a
    // confirmed-empty cart and checkout proceeded) keeps analytics without
    // paging anyone for a non-event.
    capturePosthog(payload);
    if (!isBenignValidation && ctx.suppress_alert !== true) {
      postToBackend(payload);
    }
    markRecentlyReported(payload.failure_fingerprint);

    if (payload.source && payload.stage) {
      try {
        var legacyEvent = payload.source.replace(/-/g, '_') + '_checkout_failed';
        if (window.whiskyPosthog && typeof window.whiskyPosthog.capture === 'function') {
          window.whiskyPosthog.capture(
            legacyEvent,
            funnelContext({ error_message: payload.error_message, tier_key: payload.tier_key }),
          );
        }
      } catch (e) {}
    }
  }

  function installFetchInterceptor() {
    if (window.__CART_FAILURE_FETCH_PATCHED__) return;
    window.__CART_FAILURE_FETCH_PATCHED__ = true;

    var native = window.fetch.bind(window);
    if (!window.__TP_ORIG_FETCH__) {
      window.__TP_ORIG_FETCH__ = native;
    }

    // A request that carries `wlReportToken` (cart-add-merge.js smartAdd) has a caller
    // that may report the failure itself with better context (source, stage, tier).
    // The interceptor waits this long for that report before adding its own; report()
    // drops a second report for the same token, so the two never both page.
    var OWNER_REPORT_GRACE_MS = 1500;
    // Deferred reports still waiting for their grace period. Flushed at once when the
    // page is leaving, so a shopper who navigates inside the window (the PDP upgrade
    // and the cart drawer never report themselves) does not take the report with them.
    var pendingDeferred = [];
    function flushDeferredReports() {
      var pending = pendingDeferred;
      pendingDeferred = [];
      for (var i = 0; i < pending.length; i++) {
        clearTimeout(pending[i].timer);
        report(pending[i].ctx);
      }
    }
    window.addEventListener('pagehide', flushDeferredReports);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') flushDeferredReports();
    });

    // The quiz session id on the cart (2026-10-01). whiskeylibrary-be reads the cart
    // attribute `quizSessionId` off the first box order to build box 1 from the quiz,
    // which is how a quiz taken without an email reaches the customer. The taste-profile
    // checkout writes it itself (through the raw fetch, so not here); every other add
    // that goes through window.fetch (PDP, cart drawer, offer pages, product cards,
    // theme.js product forms) gets it here, once per page view, after the add succeeds.
    // Never rename it: the backend reads this name (it also accepts _quiz_session_id).
    // The add's promise resolves only once the write is done (at most STAMP_WAIT_MS
    // later), because theme.js product forms and the checkout-redirect templates go to
    // /checkout as soon as the add answers: a write still in flight then missed the order.
    // Cart writes are kept apart from it: the write waits for cart writes already in
    // flight through this wrapper, and cart writes started while it runs wait for it, so
    // two attribute writes (the milestone and offer stamps) never overlap with it.
    var STAMP_WAIT_MS = 1500;
    var STAMP_MAX_FAILURES = 2;
    var CART_WRITE_URL = /\/cart\/(add|update|change|clear)(\.js)?(\?|#|$)/i;
    var stampedQuizSessionId = null;
    var stampInFlight = null;
    var stampFailures = 0;
    var cartWrites = [];
    window.addEventListener('pageshow', function (event) {
      // A page restored from the back/forward cache may sit on a new cart (after an order).
      if (event && event.persisted) {
        stampedQuizSessionId = null;
        stampFailures = 0;
      }
    });
    function settled(promise) {
      return promise.then(function () {}, function () {});
    }
    // Only a quiz that was finished goes on the cart (2026-10-06). quiz_sessionId is
    // written when the quiz loads, so a visitor who opened the quiz and left carried an
    // id that matches no quiz onto later orders (#64295, #64376). The quiz's submit
    // writes quiz_rework_completed with the session id (QuizReworkApp markQuizCompleted).
    function completedQuizSessionId(sessionId) {
      try {
        var marker = JSON.parse(window.localStorage.getItem('quiz_rework_completed') || 'null');
        return marker && marker.sessionId === sessionId;
      } catch (e) {
        return false;
      }
    }
    function stampQuizSessionId(currentWrite) {
      var sessionId = '';
      try {
        sessionId = (window.localStorage.getItem('quiz_sessionId') || '').trim();
      } catch (e) {}
      if (sessionId && !completedQuizSessionId(sessionId)) sessionId = '';
      if (!sessionId || stampedQuizSessionId === sessionId || stampFailures >= STAMP_MAX_FAILURES) {
        return Promise.resolve();
      }
      if (stampInFlight && stampInFlight.sessionId === sessionId) return stampInFlight.promise;
      var others = [];
      for (var i = 0; i < cartWrites.length; i++) {
        if (cartWrites[i] !== currentWrite) others.push(settled(cartWrites[i]));
      }
      var write = Promise.all(others)
        .then(function () {
          return native('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { quizSessionId: sessionId } }),
            // Survives a navigation to /checkout that starts after the 1.5s hold.
            keepalive: true,
          });
        })
        .then(function (res) {
          if (!res || !res.ok) throw new Error('quiz session stamp ' + (res && res.status));
          return res.json();
        })
        .then(function (cart) {
          // A 200 that does not carry the id (another write won) counts as a failure.
          if (cart && cart.attributes && cart.attributes.quizSessionId === sessionId) {
            stampedQuizSessionId = sessionId;
          } else {
            stampFailures += 1;
          }
        })
        .catch(function () {
          stampFailures += 1;
        })
        .then(function () {
          stampInFlight = null;
          if (stampedQuizSessionId !== sessionId && stampFailures >= STAMP_MAX_FAILURES) {
            // Given up for this page view: count it, never page.
            try {
              if (window.whiskyPosthog && typeof window.whiskyPosthog.capture === 'function') {
                window.whiskyPosthog.capture('quiz_session_cart_stamp_failed', funnelContext({ attempts: stampFailures }));
              }
            } catch (e) {}
          }
        });
      var waited = new Promise(function (resolve) {
        write.then(resolve, resolve);
        setTimeout(resolve, STAMP_WAIT_MS);
      });
      stampInFlight = { sessionId: sessionId, promise: waited };
      return waited;
    }

    window.fetch = function cartFailureAwareFetch(input, init) {
      var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      var method = (init && init.method) || (input && input.method) || 'GET';
      var reportToken = init && init.wlReportToken ? init.wlReportToken : null;
      var isCartWrite = String(method).toUpperCase() === 'POST' && CART_WRITE_URL.test(url);
      var request = isCartWrite && stampInFlight
        ? stampInFlight.promise.then(function () { return native(input, init); })
        : native(input, init);
      if (isCartWrite) {
        cartWrites.push(request);
        settled(request).then(function () {
          var at = cartWrites.indexOf(request);
          if (at !== -1) cartWrites.splice(at, 1);
        });
      }

      return request.then(function (res) {
        var stamped = null;
        try {
          if (
            res &&
            res.ok &&
            /\/cart\/add(\.js)?(\?|#|$)/i.test(url) &&
            String(method).toUpperCase() === 'POST'
          ) {
            stamped = stampQuizSessionId(request);
          }
        } catch (e) {}
        if (stamped) return stamped.then(function () { return res; });
        try {
          if (
            res &&
            !res.ok &&
            /\/cart\/(add|clear)\.js/i.test(url) &&
            String(method).toUpperCase() === 'POST'
          ) {
            var stage = /clear\.js/i.test(url) ? 'cart_clear' : 'cart_add';
            res.clone()
              .text()
              .catch(function () {
                return '';
              })
              .then(function (bodyText) {
                var summary = summarizeResponseBody(bodyText);
                var errMsg =
                  '/cart/' +
                  (stage === 'cart_clear' ? 'clear' : 'add') +
                  '.js ' +
                  res.status +
                  (summary ? ' ' + summary : '');
                var fp = fingerprint({
                  source: inferSource(),
                  stage: stage,
                  http_status: res.status,
                  error_message: errMsg,
                });
                if (!recentlyReported(fp)) {
                  var ctx = {
                    source: inferSource(),
                    stage: stage,
                    error_message: errMsg,
                    http_status: res.status,
                    shopify_response: summary || undefined,
                    error_kind: isConnectionCheck(bodyText) ? 'bot_challenge' : undefined,
                    funnel_mode: 'fetch_intercept',
                  };
                  if (reportToken) {
                    ctx._reportToken = reportToken;
                    var entry = { ctx: ctx, timer: 0 };
                    entry.timer = setTimeout(function () {
                      var at = pendingDeferred.indexOf(entry);
                      if (at !== -1) pendingDeferred.splice(at, 1);
                      report(ctx);
                    }, OWNER_REPORT_GRACE_MS);
                    pendingDeferred.push(entry);
                  } else {
                    report(ctx);
                  }
                }
              });
          }
        } catch (e) {}
        return res;
      });
    };
  }

  installFetchInterceptor();

  window.WhiskyCartFailure = {
    report: report,
    fingerprint: fingerprint,
    inferStage: inferStage,
    inferSource: inferSource,
  };
})(window);
